import React, { useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { all_routes } from "../../router/all_routes";
import { useDebounce } from "../../../core/hooks/useDebounce";

import { useDispatch, useSelector } from "react-redux";
import Toasts from "../../../core/common/toast";
import {
  deleteOfficeLocation,
  getOfficeLocation,
  statusOfficeLocation,
} from "../../../core/data/redux/officeLocationSlice";
import ChangeStatusModal from "../../../core/modals/changeStatusModal";
import DeleteModal from "../../../core/modals/deleteModal";
import PermissionGuard from "../../permissionGuard";
import { isSupportViewer } from "../../../utils/permissionUtils";

interface OfficeLocationsListProps { }

const OfficeLocationsList = (props: OfficeLocationsListProps) => {
  const tableRef = useRef<{ resetSelection: (data: any) => void }>(null);
  const [currentItem, setCurrentItem] = useState<any>(null);
  const dispatch = useDispatch();
  const officeLocationState = useSelector(
    (state: any) => state.officeLocationState
  );
  const [dataList, setDataList] = React.useState<any[]>([]);

  // Toast state variables (following SMS templates pattern)
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showDeleteSuccessToast, setShowDeleteSuccessToast] = useState(false);
  const [showStatusSuccessToast, setShowStatusSuccessToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [deleteMessage, setDeleteMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const routes = all_routes;
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 10,
  });
  const [selectedRows, setSelectedRows] = useState<any[] | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const handleReset = () => {
    if (tableRef.current) {
      tableRef.current.resetSelection([]); // Call child's function
    }
  };

  // Toast handling useEffects (following SMS templates pattern)
  React.useEffect(() => {
    if (
      officeLocationState.action === "delete" &&
      officeLocationState.loading === "fulfilled"
    ) {
      setDeleteMessage(
        officeLocationState?.data?.data?.message ||
        "Office location deleted successfully!"
      );
      setShowDeleteSuccessToast(true);
      setTimeout(() => {
        setShowDeleteSuccessToast(false);
      }, 3000);
    }
  }, [officeLocationState.action, officeLocationState.loading]);

  React.useEffect(() => {
    if (
      officeLocationState.action === "status" &&
      officeLocationState.loading === "fulfilled"
    ) {
      setStatusMessage(
        officeLocationState?.data?.data?.message ||
        "Office location status updated successfully!"
      );
      setShowStatusSuccessToast(true);
      setTimeout(() => {
        setShowStatusSuccessToast(false);
      }, 3000);
    }
  }, [officeLocationState.action, officeLocationState.loading]);

  React.useEffect(() => {
    if (officeLocationState.loading === "rejected") {
      setErrorMessage(officeLocationState.error || "Operation failed");
      setShowErrorToast(true);
      setTimeout(() => {
        setShowErrorToast(false);
      }, 4000);
    }
  }, [officeLocationState.loading, officeLocationState.error]);

  // Fetch data function
  const fetchOfficeLocations = useCallback((searchValue: string, paginationData: any) => {
    dispatch(
      getOfficeLocation({
        page: paginationData.current,
        limit: paginationData.pageSize,
        query: searchValue,
      })
    );
  }, [dispatch]);

  // Debounced search function (500ms delay)
  const debouncedSearch = useDebounce((searchValue: string, paginationData: any) => {
    fetchOfficeLocations(searchValue, paginationData);
  }, 500);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    // Reset pagination to first page when searching
    const newPagination = {
      ...pagination,
      current: 1,
    };
    setPagination(newPagination);

    // Use debounced search
    debouncedSearch(value, newPagination);
  };

  // React.useEffect(() => {
  //   fetchOfficeLocations(searchQuery, pagination);
  // }, [pagination.current, pagination.pageSize]);

  // Initial load on component mount
  React.useEffect(() => {
    fetchOfficeLocations("", { current: 1, pageSize: 10 });
  }, []);

  const handleDelete = async (response: string) => {
    if (response === "YES" && currentItem) {
      console.log("Delete item:", currentItem);
      await dispatch(deleteOfficeLocation({ id: currentItem?.id }));
      await dispatch(
        getOfficeLocation({
          page: pagination.current,
          limit: pagination.pageSize,
          query: searchQuery,
        })
      );
    }
    setCurrentItem(null);
  };

  const handleChangeStatus = async (response: string) => {
    console.log("response", response);
    if (response === "YES" && currentItem) {
      await dispatch(
        statusOfficeLocation({
          ids: [currentItem.id],
          status: currentItem.status ? false : true,
        })
      );
      await dispatch(
        getOfficeLocation({
          page: pagination.current,
          limit: pagination.pageSize,
          query: searchQuery,
        })
      );
    }
    setCurrentItem(null);
  };

  React.useEffect(() => {
    if (officeLocationState.loading === "fulfilled") {
      const result = officeLocationState?.data || null;
      if (result.data && result.data.length > 0) {
        let officeLocations = result.data.map((item: any) => ({
          id: item.id,
          name: item.name,
          address: item.address,
          phone: item.phone,
          email: item.email,
          key: item.id,
          status: item.status,
        }));
        setDataList(officeLocations);
        setPagination({
          ...pagination,
          total: result.total || officeLocations.length,
          current: result.page || 1,
          pageSize: result.limit || 10,
        });
      } else {
        setDataList([]);
      }
    }
  }, [officeLocationState.loading]);

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      render: (text: string, record: any, index: number) => (
        <>
          <div>
            {pagination.current
              ? index +
              pagination.current * pagination.pageSize -
              pagination.pageSize +
              1
              : index + 1}
          </div>
        </>
      ),
      sorter: (a: any, b: any) => a.id.length - b.id.length,
    },

    {
      title: "Name",
      dataIndex: "name",
      sorter: (a: any, b: any) => a.name.length - b.name.length,
    },
    {
      title: "Address",
      dataIndex: "address",
      sorter: (a: any, b: any) => a.address.length - b.address.length,
    },
    // {
    //   title: "Phone",
    //   dataIndex: "phone",
    //   sorter: (a: any, b: any) => a.phone.length - b.phone.length,
    // },
    // {
    //   title: "Email",
    //   dataIndex: "email",
    //   sorter: (a: any, b: any) => a.email.length - b.email.length,
    // },
    {
      title: "Status",
      dataIndex: "Status",
      render: (text: string, record: any) =>
        isSupportViewer() ? (
          <span
            className={`badge ${
              record.status ? "badge-success" : "badge-danger"
            } d-inline-flex align-items-center badge-xs`}
          >
            <i className="ti ti-point-filled me-1" />
            {record.status ? "Active" : "Inactive"}
          </span>
        ) : (
          <Link
            to="#"
            data-bs-toggle="modal"
            data-inert={true}
            data-bs-target="#change_status_modal"
            onClick={() => setCurrentItem(record)}
          >
            <span
              className={`badge ${
                record.status ? "badge-success" : "badge-danger"
              } d-inline-flex align-items-center badge-xs`}
            >
              <i className="ti ti-point-filled me-1" />
              {record.status ? "Active" : "Inactive"}
            </span>
          </Link>
        ),
      sorter: (a: any, b: any) => a.status.length - b.status.length,
    },
    ...(!isSupportViewer()
      ? [
          {
            title: "Actions",
            dataIndex: "actions",
            render: (text: string, record: any, index: number) => (
              <div className="action-icon d-inline-flex">
                <PermissionGuard route="office-location/edit">
                  <Link
                    to={routes.officeLocation.edit.replace(":id", record.id)}
                    className="me-2"
                  >
                    <i className="ti ti-edit" />
                  </Link>
                </PermissionGuard>
              </div>
            ),
          },
        ]
      : []),
  ];

  const handlePagination = (pagination: any) => {
    console.log("Pagination changed:", pagination);
    setPagination({
      current: pagination.page,
      pageSize: pagination.pageSize,
      total: pagination.total || 10,
    });
    dispatch(
      getOfficeLocation({
        page: pagination.current,
        limit: pagination.pageSize,
        query: searchQuery,
      })
    );
    // dispatch(getOfficeLocation({
    //   page: page,
    //   limit: pageSize
    // }));
  };

  const handleOnRowSelection = (selectedRowKeys: any) => {
    // if (allowedPermissions.includes('office-location/change-status') || userType === 'ADMIN' || userType === 'SUBADMIN') {
    setSelectedRows(selectedRowKeys);
    setStatus(null);
    // }
  };

  const handleStatusChange = async () => {
    await dispatch(
      statusOfficeLocation({
        ids: selectedRows,
        status: status === "Active" ? true : false,
      })
    );
    setSelectedRows(null);
    setStatus(null);
    handleReset();
    dispatch(
      getOfficeLocation({
        page: pagination.current,
        limit: pagination.pageSize,
      })
    );
  };

  return (
    <div>
      <>
        {/* Toast Messages (following SMS templates pattern) */}
        <Toasts
          show={showErrorToast}
          message={errorMessage}
          title={"Failed"}
          type={"danger"}
        />
        <Toasts
          show={showDeleteSuccessToast}
          message={deleteMessage}
          title={"Success"}
          type={"success"}
        />
        <Toasts
          show={showStatusSuccessToast}
          message={statusMessage}
          title={"Success"}
          type={"success"}
        />

        <div className="page-wrapper">
          <div className="content">
            {/* Page Header */}
            <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">Office Locations</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={routes.adminDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="#">Office Locations</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Locations List
                    </li>
                  </ol>
                </nav>
              </div>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                <div className="mb-2">
                  <PermissionGuard route="office-location/add">
                    <Link
                      to={routes.officeLocation.add}
                      className="btn btn-primary d-flex align-items-center"
                    >
                      <i className="ti ti-square-rounded-plus me-2" />
                      Add Office
                    </Link>
                  </PermissionGuard>
                </div>
              </div>
            </div>
            {/* /Page Header */}
            {/* Filter Section */}
            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                <h4 className="mb-4 mt-3">Locations List</h4>
                <div className="d-flex align-items-center flex-wrap">
                  {/* <div className="input-icon-start mb-3 me-2 position-relative">
                    <PredefinedDateRanges />
                  </div>
                  <div className="dropdown mb-3 me-2">
                    <Link
                      to="#"
                      className="btn btn-outline-light bg-white dropdown-toggle"
                      data-bs-toggle="dropdown"
                      data-bs-auto-close="outside"
                    >
                      <i className="ti ti-filter me-2" />
                      Filter
                    </Link>
                    <div
                      className="dropdown-menu drop-width"
                      ref={dropdownMenuRef}
                    >
                      <form>
                        <div className="d-flex align-items-center border-bottom p-3">
                          <h4>Filter</h4>
                        </div>
                        <div className="p-3 border-bottom">
                          <div className="row">
                            <div className="col-md-12">
                              <div className="mb-0">
                                <label className="form-label">Users</label>
                                
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="p-3 d-flex align-items-center justify-content-end">
                          <Link to="#" className="btn btn-light me-3">
                            Reset
                          </Link>
                          <Link
                            to="#"
                            className="btn btn-primary"
                            //onClick={handleApplyClick}
                          >
                            Apply
                          </Link>
                        </div>
                      </form>
                    </div>
                  </div> */}
                  {selectedRows && selectedRows.length > 0 && (
                    <PermissionGuard route="office-location/change-status">
                      <div className="dropdown me-3 mb-0">
                        <Link
                          to="#"
                          className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                          data-bs-toggle="dropdown"
                        >
                          {status || "Select Status"}
                        </Link>
                        <ul className="dropdown-menu  dropdown-menu-end p-3">
                          <li>
                            <Link
                              to="#"
                              className={`dropdown-item rounded-1 ${status === "Active" ? "active" : ""
                                }`}
                              onClick={() => {
                                setStatus("Active");
                              }}
                            >
                              Active
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="#"
                              className={`dropdown-item rounded-1 ${status === "Inactive" ? "active" : ""
                                }`}
                              onClick={() => {
                                setStatus("Inactive");
                              }}
                            >
                              Inactive
                            </Link>
                          </li>
                        </ul>
                      </div>
                      {status && (
                        <div className="dropdown me-3 mb-0">
                          <button
                            className="btn btn-primary w-100"
                            type="button"
                            onClick={handleStatusChange}
                          >
                            Change Status
                          </button>
                        </div>
                      )}
                    </PermissionGuard>
                  )}
                  {/* <div className="dropdown mb-3">
                    <Link
                      to="#"
                      className="btn btn-outline-light bg-white dropdown-toggle"
                      data-bs-toggle="dropdown"
                    >
                      <i className="ti ti-sort-ascending-2 me-2" />
                      Sort by A-Z
                    </Link>
                    <ul className="dropdown-menu p-3">
                      <li>
                        <Link to="#" className="dropdown-item rounded-1 active">
                          Ascending
                        </Link>
                      </li>
                      <li>
                        <Link to="#" className="dropdown-item rounded-1">
                          Descending
                        </Link>
                      </li>
                      <li>
                        <Link to="#" className="dropdown-item rounded-1">
                          Recently Viewed
                        </Link>
                      </li>
                      <li>
                        <Link to="#" className="dropdown-item rounded-1">
                          Recently Added
                        </Link>
                      </li>
                    </ul>
                  </div> */}
                </div>
              </div>
              {/* User List */}
              <div className="card-body p-0 py-3">
                <Table
                  columns={columns}
                  dataSource={dataList}
                  Selection={true}
                  paginationConfig={pagination}
                  handlePagination={handlePagination}
                  handleSearch={handleSearch}
                  loading={officeLocationState.loading === "pending"}
                  onRowSelection={handleOnRowSelection}
                  ref={tableRef}
                >
                  {/* Custom search component with enhanced placeholder */}
                  <div className="col-sm-12 col-md-6">
                    <div
                      id="DataTables_Table_0_filter"
                      className="dataTables_filter text-end mb-0"
                    >
                      <label>
                        {" "}
                        <input
                          type="search"
                          className="form-control form-control-sm"
                          placeholder="Search by name, address, phone, email, or status (active/inactive)"
                          aria-controls="DataTables_Table_0"
                          onChange={(e) => {
                            handleSearch(e.target.value);
                          }}
                        />
                      </label>
                    </div>
                  </div>
                </Table>
              </div>
              {/* /User List */}
            </div>
            {/* /Filter Section */}
            <div className="row align-items-center">
              <div className="col-md-12">
                <div className="datatable-paginate mt-4" />
              </div>
            </div>
          </div>
        </div>
        {/* /Page Wrapper */}
      </>
      <DeleteModal handleDelete={handleDelete} />
      <ChangeStatusModal handleChangeStatus={handleChangeStatus} />
    </div>
  );
};

export default OfficeLocationsList;
